import { useEffect, useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import useSWRMutation from 'swr/mutation'
import { z } from 'zod'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@ludus/ui'
import type { PipelineId } from '@ludus/core'
import { FlowApiError, createFlow, type FlowSummary } from '@/lib/api/flows.ts'
import { apiErrorMessage } from '@/shared/feedback/apiErrorMessage.ts'

interface NewFlowDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  pipelineId: PipelineId
  /**
   * Called with the created flow; the caller re-reads the list and navigates.
   *
   * Deliberately `void` and not `Promise`: it runs after the flow is already
   * persisted, so nothing it does may fail the create. A caller that awaits
   * something here owns that promise and its rejection.
   */
  onCreated: (flow: FlowSummary) => void
}

const newFlowSchema = z.object({ name: z.string().trim().min(1, 'required') })

type NewFlowFormValues = z.infer<typeof newFlowSchema>

/**
 * A name is the whole form, because that is all a new flow can be given: the
 * engine mints a draft with one unconfigured trigger and `enabled: false`, and
 * everything else is drawn on the canvas.
 */
export function NewFlowDialog({ open, onOpenChange, pipelineId, onCreated }: NewFlowDialogProps) {
  const { t } = useTranslation()
  const formId = useId()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewFlowFormValues>({
    resolver: zodResolver(newFlowSchema),
    defaultValues: { name: '' },
  })

  const [submitError, setSubmitError] = useState<FlowApiError | null>(null)

  const { trigger, isMutating } = useSWRMutation(
    `/pipelines/${pipelineId}/flows:create`,
    (_key: string, { arg }: { arg: { name: string } }) => createFlow(pipelineId, arg),
  )

  useEffect(() => {
    if (open) {
      reset({ name: '' })
      setSubmitError(null)
    }
  }, [open, reset])

  const onSubmit = handleSubmit(async (values) => {
    // The submit button is disabled while mutating; the Enter key is not.
    if (isMutating) return
    setSubmitError(null)

    let created: FlowSummary
    try {
      created = await trigger({ name: values.name })
    } catch (err) {
      setSubmitError(
        err instanceof FlowApiError
          ? err
          : new FlowApiError({ kind: 'unknown', status: null, message: 'submit failed' }),
      )
      return
    }

    // The create is the only thing inside that boundary, because past this line
    // the flow exists. `onCreated` re-reads a catalogue and navigates, and SWR's
    // bound `mutate()` rejects when the revalidation it triggers fails — which,
    // awaited in the `try` above, told the operator "submit failed" over a
    // dialog that stayed open, inviting a second flow with the same name.
    onOpenChange(false)
    onCreated(created)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="min-w-0">
            <DialogTitle>
              {t('pages.pipelines.settings.flows.new.title', { defaultValue: 'New flow' })}
            </DialogTitle>
            <DialogDescription>
              {t('pages.pipelines.settings.flows.new.description', {
                defaultValue:
                  'The flow starts off and empty. Draw its trigger, conditions and action on the canvas, then turn it on.',
              })}
            </DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>
        <DialogBody className="py-5">
          <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-5">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                {t('pages.pipelines.settings.flows.new.name', { defaultValue: 'Name' })}
              </span>
              <Input
                {...register('name')}
                autoFocus
                placeholder={t('pages.pipelines.settings.flows.new.namePlaceholder', {
                  defaultValue: 'e.g. Paid order → Delivery card',
                })}
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <span role="alert" className="text-[11px] font-medium text-toast-error-text">
                  {t('pages.pipelines.settings.flows.new.nameRequired', {
                    defaultValue: 'Enter a name.',
                  })}
                </span>
              )}
            </label>
            {submitError && (
              <p role="alert" className="text-[11px] font-medium text-toast-error-text">
                {apiErrorMessage(submitError, t, 'pages.pipelines.settings.flows.new')}
              </p>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isMutating}
          >
            {t('pages.pipelines.settings.flows.new.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="submit" form={formId} variant="primary" size="sm" disabled={isMutating}>
            {t('pages.pipelines.settings.flows.new.create', { defaultValue: 'Create flow' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
